import type { LanguageCode } from '../types'

export const MULTILINGUAL_END_INTENT_PHRASES: Record<LanguageCode, string[]> = {
  en: [
    'done', "that's all", "thats all", 'finished', 'end chat', 'end the chat',
    'bye', 'goodbye', 'thank you', 'thanks', 'no more', "i'm done", "im done",
    'done sharing', 'enough', 'nothing else', 'that is all', 'stop', 'exit',
    'all done', 'complete', 'completed',
  ],
  hi: [
    'बस इतना ही', 'धन्यवाद', 'हो गया', 'समाप्त', 'अलविदा', 'बस',
    'कुछ नहीं', 'काफी है', 'बंद करो', 'पूर्ण', 'ठीक है धन्यवाद', 'बस धन्यवाद',
  ],
  bn: [
    'হয়ে গেছে', 'ধন্যবাদ', 'আর কিছু নেই', 'সমাপ্ত', 'বিদায়', 'যথেষ্ট', 'শেষ',
  ],
  ta: [
    'முடிந்தது', 'நன்றி', 'அவ்வளவுதான்', 'போதும்', 'முடிவு', 'விடைபெறுகிறேன்',
  ],
  te: [
    'పూర్తయింది', 'ధన్యవాదాలు', 'ఇంతే', 'చాలు', 'ముగింపు', 'సెలవు',
  ],
  mr: [
    'झाले', 'धन्यवाद', 'एवढेच', 'पुरे', 'समाप्त', 'निरोप', 'संपले',
  ],
  gu: [
    'પૂરું થયું', 'આભાર', 'બસ આટલું જ', 'થઈ ગયું', 'પૂરું', 'આવજો', 'બસ',
  ],
}

export interface TranslationDictionary {
  auth: {
    title: string
    subtitle: string
    doctorTitle: string
    doctorSubtitle: string
    patient: string
    doctor: string
    patientRoleTag: string
    doctorRoleTag: string
    patientAbhaLabel: string
    doctorAbhaLabel: string
    abhaFormat: string
    doctorFormat: string
    requestOtp: string
    doctorRequestOtp: string
    sendingOtp: string
    welcome: string
    otpSent: string
    doctorOtpSent: string
    changeAbha: string
    changeHpId: string
    sentToMobile: string
    enterOtp: string
    verifyAndEnter: string
    verifying: string
    register: string
    registerPatientPrompt: string
    registerPatientDesc: string
    registerPatientButton: string
    registerDoctorPrompt: string
    registerDoctorDesc: string
    registerDoctorButton: string
    footer: string
  }
  header: {
    dashboard: string
    profile: string
    viewSummary: string
    submitDetails: string
    saving: string
    walkInPatient: string
    abhaLinked: string
    changeLanguage: string
  }
  dashboard: {
    abhaLinked: string
    profile: string
    addDetails: string
    signOut: string
    personalDetails: string
    activeMedications: string
    pastMedications: string
    recentIntakeSessions: string
    medicalDocuments: string
    startIntakeConsultation: string
    noRecords: string
    allergies: string
    chronicConditions: string
    emergencyContact: string
    noAllergies: string
    noChronic: string
    viewSummary: string
  }
  chat: {
    clinicalIntake: string
    chatCompleted: string
    inputPlaceholder: string
    send: string
    listening: string
    speak: string
    suggestedQuickReplies: string
    finishChat: string
    continueChat: string
    restartChat: string
    chatEndedTitle: string
    chatEndedDesc: string
    generatingQuestion: string
    endIntentPrompt?: string
    endIntentYes?: string
    endIntentNo?: string
  }
  scanner: {
    title: string
    uploaded: string
    takePhoto: string
    uploadFile: string
    dropzone: string
    scanning: string
  }
  doctor: {
    hprVerified: string
    signOut: string
    stationTitle: string
    clinicalReviewTitle: string
    portalDescription: string
    opdRegular: string
    dutyActive: string
    today: string
    registeredPatients: string
    liveRecords: string
    triageRedFlags: string
    urgentAttention: string
    vitalsStable: string
    digitizedPrescriptions: string
    verifiedOcr: string
    consultationSessions: string
    aiIntakeConsultations: string
    searchPlaceholder: string
    redFlagsOnly: string
    withDocuments: string
    refreshRecords: string
    patientQueue: string
    colPatient: string
    colDemographics: string
    colTriage: string
    colDocuments: string
    colActions: string
    noPatients: string
    reviewDossier: string
    tabConsultations: string
    tabPrescriptions: string
    tabMedications: string
    tabBaseline: string
    closeDossier: string
    patientLanguage: string
    translateTo: string
    translating: string
    showOriginal: string
    showTranslated: string
    translatedBadge: string
    differentLanguageNotice: string
    translationUnavailable: string
    selectLanguage: string
  }
  summary: {
    title: string
    subtitle: string
    print: string
    close: string
    aiTab: string
    detailsTab: string
    generatePromptTitle: string
    generatePromptDesc: string
    generateButton: string
    loadingTitle: string
    loadingDesc: string
    doctorReviewNote: string
    regenerate: string
    noData: string
    patientInfo: string
    chiefComplaint: string
    history: string
    clinicalNarrative: string
    documents: string
    ayushAssessment: string
    redFlags: string
    recommendations: string
    patientInformation: string
    age: string
    years: string
    gender: string
    language: string
    duration: string
    uploadedDocuments: string
    conversationLog: string
    patientTurns: string
    medications: string
    labResults: string
    parameter: string
    value: string
    unit: string
    status: string
    abnormal: string
    normal: string
    noEntities: string
    noSessionData: string
    noSessionDataDesc: string
    done: string
    footerBrand: string
  }
}

export const TRANSLATIONS: Record<LanguageCode, TranslationDictionary> = {
  en: {
    auth: {
      title: 'Sign In with ABHA',
      subtitle: 'Ayushman Bharat Health Account & OTP Authentication',
      doctorTitle: 'Clinician Portal • Sign In with HP ID',
      doctorSubtitle: 'National Healthcare Professional Registry (HPR) & OTP Authentication',
      patient: 'Patient',
      doctor: 'Doctor',
      patientRoleTag: 'Patient (मरीज़)',
      doctorRoleTag: 'Doctor (चिकित्सक)',
      patientAbhaLabel: 'Patient ABHA ID / Health Card Number',
      doctorAbhaLabel: 'HP ID (Healthcare Professional ID)',
      abhaFormat: 'Standard 14-digit national identity format (e.g. 14-2345-6789-1011)',
      doctorFormat: 'Format: HP-[State]-[Number] (e.g. HP-MH-84729)',
      requestOtp: 'Request ABHA OTP',
      doctorRequestOtp: 'Request HP OTP',
      sendingOtp: 'Sending OTP...',
      welcome: 'Welcome',
      otpSent: 'ABHA OTP Dispatched',
      doctorOtpSent: 'Clinician OTP Dispatched',
      changeAbha: 'Change ABHA ID',
      changeHpId: 'Change HP ID',
      sentToMobile: 'Sent to registered mobile',
      enterOtp: 'Enter 6-Digit OTP',
      verifyAndEnter: 'Verify & Enter Portal',
      verifying: 'Verifying...',
      register: 'Register',
      registerPatientPrompt: "Don't have an ABHA ID yet?",
      registerPatientDesc: 'Self-enroll in seconds to generate your digital ABHA health card.',
      registerPatientButton: 'Register & Create ABHA ID',
      registerDoctorPrompt: 'Not registered on HPR yet?',
      registerDoctorDesc: 'Enrol via National Healthcare Professional Registry to access clinical dossiers.',
      registerDoctorButton: 'Register as Healthcare Professional (HP ID)',
      footer: 'Sanjivani Clinical AI • Built for Indian Healthcare Facilities & AYUSH Centers',
    },
    header: {
      dashboard: 'Dashboard',
      profile: 'Profile',
      viewSummary: 'View Summary',
      submitDetails: 'Submit Details',
      saving: 'Saving...',
      walkInPatient: 'Walk-in Patient',
      abhaLinked: 'ABHA Linked',
      changeLanguage: 'Change language',
    },
    dashboard: {
      abhaLinked: 'ABHA Linked',
      profile: 'Profile',
      addDetails: 'Add Details',
      signOut: 'Sign Out',
      personalDetails: 'Personal Details',
      activeMedications: 'Active Medications',
      pastMedications: 'Past Medications',
      recentIntakeSessions: 'Recent Intake Consultations',
      medicalDocuments: 'Medical Documents',
      startIntakeConsultation: 'Start Intake Consultation',
      noRecords: 'No records found',
      allergies: 'Allergies',
      chronicConditions: 'Chronic Conditions',
      emergencyContact: 'Emergency Contact',
      noAllergies: 'No known drug allergies reported',
      noChronic: 'None reported',
      viewSummary: 'View Summary',
    },
    chat: {
      clinicalIntake: 'Clinical Intake Consultation',
      chatCompleted: 'Chat Completed',
      inputPlaceholder: 'Speak or type your symptoms in your language...',
      send: 'Send',
      listening: 'Listening...',
      speak: 'Voice Input',
      suggestedQuickReplies: 'Suggested Responses',
      finishChat: 'Finish Consultation',
      continueChat: 'Continue Consultation',
      restartChat: 'Restart Chat',
      chatEndedTitle: 'Intake Consultation Concluded',
      chatEndedDesc: 'Your symptoms and clinical history have been documented. You can view your summary or submit details to your health record.',
      generatingQuestion: 'Generating clinical question...',
      endIntentPrompt: 'Did you mean to end the chat?',
      endIntentYes: 'Yes, End Chat',
      endIntentNo: 'No',
    },
    scanner: {
      title: 'Medical Document Scanner',
      uploaded: 'uploaded',
      takePhoto: 'Take Photo',
      uploadFile: 'Upload File',
      dropzone: 'Drag & drop prescription or test report image here, or click to browse',
      scanning: 'Scanning & Digitizing document...',
    },
    doctor: {
      hprVerified: 'HPR Verified Clinician',
      signOut: 'Sign Out',
      stationTitle: 'Doctor Clinical Oversight Station (EHR Database Access)',
      clinicalReviewTitle: 'Clinical Review & Patient Records Registry',
      portalDescription: 'Live healthcare provider portal connecting to all registered patients in the database. Inspect AI-digitized intake transcripts, multimodal OCR verified prescriptions, and AYUSH Prakriti assessments.',
      opdRegular: 'OPD Regular',
      dutyActive: 'Duty: Active',
      today: 'Today',
      registeredPatients: 'Registered Patients',
      liveRecords: 'Live database records',
      triageRedFlags: 'Triage Red Flags',
      urgentAttention: 'Urgent attention needed',
      vitalsStable: 'All vitals stable',
      digitizedPrescriptions: 'Digitized Prescriptions',
      verifiedOcr: 'Verified via AI Vision OCR',
      consultationSessions: 'Consultation Sessions',
      aiIntakeConsultations: 'AI intake consultations',
      searchPlaceholder: 'Search patients by name or ABHA ID...',
      redFlagsOnly: 'Red Flags Only',
      withDocuments: 'With Documents',
      refreshRecords: 'Refresh Patient Records',
      patientQueue: 'Live Patient Review Queue',
      colPatient: 'Patient',
      colDemographics: 'Demographics',
      colTriage: 'Triage Status',
      colDocuments: 'Documents',
      colActions: 'Actions',
      noPatients: 'No patients found matching your search criteria.',
      reviewDossier: 'Review Dossier',
      tabConsultations: 'AI Consultations',
      tabPrescriptions: 'Prescriptions',
      tabMedications: 'Medications',
      tabBaseline: 'AYUSH & Baseline',
      closeDossier: 'Close Dossier',
      patientLanguage: 'Patient Language',
      translateTo: 'Translate to',
      translating: 'Translating...',
      showOriginal: 'Original',
      showTranslated: 'Translated',
      translatedBadge: 'AI Clinical Translation',
      differentLanguageNotice: 'This consultation was conducted in a different language.',
      translationUnavailable: 'Translation unavailable',
      selectLanguage: 'Select Language',
    },
    summary: {
      title: 'Clinical Session Summary',
      subtitle: 'Ready for physician review',
      print: 'Print',
      close: 'Close summary',
      aiTab: 'AI Summary',
      detailsTab: 'Details',
      generatePromptTitle: 'Generate AI Summary',
      generatePromptDesc: 'Sanjivani AI will synthesize your chat history and all uploaded documents into a structured clinical summary for the physician.',
      generateButton: 'Generate Summary',
      loadingTitle: 'Sanjivani AI is synthesizing your clinical summary…',
      loadingDesc: 'Reviewing chat & scanned documents',
      doctorReviewNote: 'Summary generated for doctor review',
      regenerate: 'Regenerate',
      noData: 'No clinical data collected yet. Start a consultation or upload documents.',
      patientInfo: 'Patient Info',
      chiefComplaint: 'Chief Complaint',
      history: 'History of Presenting Illness',
      clinicalNarrative: 'Clinical Narrative',
      documents: 'Documents & Investigations',
      ayushAssessment: 'Ayush Assessment',
      redFlags: 'Red Flags',
      recommendations: 'Recommendations',
      patientInformation: 'Patient Information',
      age: 'Age',
      years: 'years',
      gender: 'Gender',
      language: 'Language',
      duration: 'Duration',
      uploadedDocuments: 'Uploaded Documents',
      conversationLog: 'Conversation Log',
      patientTurns: 'patient turns',
      medications: 'Medications',
      labResults: 'Lab Results',
      parameter: 'Parameter',
      value: 'Value',
      unit: 'Unit',
      status: 'Status',
      abnormal: 'Abnormal',
      normal: 'Normal',
      noEntities: 'No structured entities extracted from this document.',
      noSessionData: 'No session data collected yet.',
      noSessionDataDesc: 'Start chatting or scan a document to see details here.',
      done: 'Done',
      footerBrand: 'Generated by Sanjivani AI — Ministry of Ayush · SIH 2026',
    },
  },

  hi: {
    auth: {
      title: 'आभा (ABHA) से साइन इन करें',
      subtitle: 'आयुष्मान भारत स्वास्थ्य खाता एवं ओटीपी प्रमाणीकरण',
      doctorTitle: 'चिकित्सक पोर्टल • HP ID से साइन इन करें',
      doctorSubtitle: 'राष्ट्रीय स्वास्थ्य पेशेवर रजिस्ट्री (HPR) एवं ओटीपी प्रमाणीकरण',
      patient: 'मरीज़',
      doctor: 'चिकित्सक',
      patientRoleTag: 'मरीज़ (Patient)',
      doctorRoleTag: 'चिकित्सक (Doctor)',
      patientAbhaLabel: 'मरीज़ आभा आईडी / स्वास्थ्य कार्ड नंबर',
      doctorAbhaLabel: 'HP ID (स्वास्थ्य पेशेवर आईडी)',
      abhaFormat: 'मानक 14-अंकीय राष्ट्रीय पहचान प्रारूप (उदा. 14-2345-6789-1011)',
      doctorFormat: 'प्रारूप: HP-[राज्य]-[संख्या] (उदा. HP-MH-84729)',
      requestOtp: 'आभा ओटीपी प्राप्त करें',
      doctorRequestOtp: 'HP OTP प्राप्त करें',
      sendingOtp: 'ओटीपी भेजा जा रहा है...',
      welcome: 'स्वागत है',
      otpSent: 'आभा ओटीपी भेज दिया गया है',
      doctorOtpSent: 'चिकित्सक ओटीपी भेज दिया गया है',
      changeAbha: 'आभा आईडी बदलें',
      changeHpId: 'HP ID बदलें',
      sentToMobile: 'पंजीकृत मोबाइल नंबर पर भेजा गया',
      enterOtp: '6 अंकों का ओटीपी दर्ज करें',
      verifyAndEnter: 'सत्यापित करें और प्रवेश करें',
      verifying: 'सत्यापन हो रहा है...',
      register: 'पंजीकरण करें',
      registerPatientPrompt: 'क्या आपके पास अभी तक ABHA ID नहीं है?',
      registerPatientDesc: 'अपना डिजिटल आभा स्वास्थ्य कार्ड बनाने के लिए तुरंत पंजीकरण करें।',
      registerPatientButton: 'पंजीकरण करें और आभा आईडी बनाएं',
      registerDoctorPrompt: 'क्या आप अभी तक HPR पर पंजीकृत नहीं हैं?',
      registerDoctorDesc: 'क्लिनिकल पोर्टल तक पहुँचने के लिए स्वास्थ्य पेशेवर के रूप में पंजीकरण करें।',
      registerDoctorButton: 'स्वास्थ्य पेशेवर (HP ID) पंजीकृत करें',
      footer: 'संजीवनी क्लिनिकल एआई • भारतीय स्वास्थ्य केंद्रों एवं आयुष संस्थानों के लिए निर्मित',
    },
    header: {
      dashboard: 'डैशबोर्ड',
      profile: 'प्रोफ़ाइल',
      viewSummary: 'सारांश देखें',
      submitDetails: 'विवरण सुरक्षित करें',
      saving: 'सुरक्षित हो रहा है...',
      walkInPatient: 'सामान्य मरीज़',
      abhaLinked: 'आभा लिंक किया गया',
      changeLanguage: 'भाषा बदलें',
    },
    dashboard: {
      abhaLinked: 'आभा लिंक है',
      profile: 'प्रोफ़ाइल',
      addDetails: 'लक्षण जोड़ें',
      signOut: 'लॉग आउट',
      personalDetails: 'व्यक्तिगत विवरण',
      activeMedications: 'सक्रिय दवाएं',
      pastMedications: 'पिछली दवाएं',
      recentIntakeSessions: 'हालिया परामर्श सत्र',
      medicalDocuments: 'चिकित्सा दस्तावेज़',
      startIntakeConsultation: 'नया परामर्श शुरू करें',
      noRecords: 'कोई रिकॉर्ड नहीं मिला',
      allergies: 'एलर्जी',
      chronicConditions: 'दीर्घकालिक बीमारियां',
      emergencyContact: 'आपातकालीन संपर्क',
      noAllergies: 'कोई ज्ञात दवा एलर्जी दर्ज नहीं है',
      noChronic: 'कोई दर्ज नहीं',
      viewSummary: 'सारांश देखें',
    },
    chat: {
      clinicalIntake: 'क्लिनिकल परामर्श एवं लक्षण पूछताछ',
      chatCompleted: 'बातचीत समाप्त',
      inputPlaceholder: 'अपनी भाषा में बोलें या अपने लक्षण टाइप करें...',
      send: 'भेजें',
      listening: 'सुन रहे हैं...',
      speak: 'बोलकर बताएं',
      suggestedQuickReplies: 'सुझाए गए उत्तर',
      finishChat: 'परामर्श समाप्त करें',
      continueChat: 'परामर्श जारी रखें',
      restartChat: 'फिर से शुरू करें',
      chatEndedTitle: 'परामर्श समाप्त हो गया है',
      chatEndedDesc: 'आपके लक्षण और स्वास्थ्य इतिहास रिकॉर्ड कर लिए गए हैं। आप सारांश देख सकते हैं या रिकॉर्ड सुरक्षित कर सकते हैं।',
      generatingQuestion: 'चिकित्सीय प्रश्न तैयार किया जा रहा है...',
      endIntentPrompt: 'क्या आप बातचीत समाप्त करना चाहते हैं?',
      endIntentYes: 'हाँ, बातचीत समाप्त करें',
      endIntentNo: 'नहीं',
    },
    scanner: {
      title: 'दस्तावेज़ स्कैनर',
      uploaded: 'अपलोड किए गए',
      takePhoto: 'फोटो लें',
      uploadFile: 'फ़ाइल चुनें',
      dropzone: 'पर्चे या जांच रिपोर्ट की छवि यहां खींचें या चुनने के लिए क्लिक करें',
      scanning: 'दस्तावेज़ स्कैन व डिजिटाइज़ हो रहा है...',
    },
    doctor: {
      hprVerified: 'HPR सत्यापित चिकित्सक',
      signOut: 'लॉग आउट',
      stationTitle: 'चिकित्सक नैदानिक निगरानी स्टेशन (EHR डेटाबेस)',
      clinicalReviewTitle: 'नैदानिक समीक्षा और रोगी रिकॉर्ड रजिस्ट्री',
      portalDescription: 'डेटाबेस में सभी पंजीकृत रोगियों से जुड़ा स्वास्थ्य प्रदाता पोर्टल। AI-डिजिटाइज़्ड ट्रांसक्रिप्ट, OCR सत्यापित पर्चे और आयुष प्रकृति का निरीक्षण करें।',
      opdRegular: 'ओपीडी सामान्य',
      dutyActive: 'ड्यूटी: सक्रिय',
      today: 'आज',
      registeredPatients: 'पंजीकृत मरीज',
      liveRecords: 'सक्रिय डेटाबेस रिकॉर्ड',
      triageRedFlags: 'आपातकालीन रेड फ्लैग',
      urgentAttention: 'तत्काल ध्यान आवश्यक',
      vitalsStable: 'सभी महत्वपूर्ण लक्षण स्थिर',
      digitizedPrescriptions: 'डिजिटाइज़्ड नुस्खे',
      verifiedOcr: 'AI विज़न OCR द्वारा सत्यापित',
      consultationSessions: 'परामर्श सत्र',
      aiIntakeConsultations: 'AI प्राथमिक परामर्श',
      searchPlaceholder: 'नाम या ABHA ID से मरीज खोजें...',
      redFlagsOnly: 'केवल रेड फ्लैग',
      withDocuments: 'दस्तावेजों सहित',
      refreshRecords: 'रोगी रिकॉर्ड रीफ्रेश करें',
      patientQueue: 'सक्रिय रोगी समीक्षा कतार',
      colPatient: 'मरीज',
      colDemographics: 'जनसांख्यिकी',
      colTriage: 'ट्राइएज स्थिति',
      colDocuments: 'दस्तावेज़',
      colActions: 'कार्रवाई',
      noPatients: 'खोज मानदंडों से मेल खाता कोई मरीज नहीं मिला।',
      reviewDossier: 'डोज़ियर देखें',
      tabConsultations: 'AI परामर्श',
      tabPrescriptions: 'दवा के नुस्खे',
      tabMedications: 'सक्रिय दवाएं',
      tabBaseline: 'आयुष एवं मेडिकल बेसलाइन',
      closeDossier: 'डोज़ियर बंद करें',
      patientLanguage: 'रोगी की भाषा',
      translateTo: 'अनुवाद करें',
      translating: 'अनुवाद हो रहा है...',
      showOriginal: 'मूल विवरण',
      showTranslated: 'अनुवादित',
      translatedBadge: 'AI नैदानिक अनुवाद',
      differentLanguageNotice: 'यह परामर्श एक अलग भाषा में दर्ज किया गया था।',
      translationUnavailable: 'अनुवाद अनुपलब्ध',
      selectLanguage: 'भाषा चुनें',
    },
    summary: {
      title: 'चिकित्सीय सत्र सारांश',
      subtitle: 'चिकित्सक की समीक्षा के लिए तैयार',
      print: 'प्रिंट करें',
      close: 'सारांश बंद करें',
      aiTab: 'एआई सारांश',
      detailsTab: 'विस्तृत विवरण',
      generatePromptTitle: 'एआई सारांश तैयार करें',
      generatePromptDesc: 'संजीवनी एआई आपकी बातचीत के इतिहास और अपलोड किए गए दस्तावेजों को चिकित्सक के लिए संरचित सारांश में संश्लेषित करेगा।',
      generateButton: 'सारांश तैयार करें',
      loadingTitle: 'संजीवनी एआई आपका नैदानिक सारांश तैयार कर रहा है…',
      loadingDesc: 'बातचीत और स्कैन किए गए दस्तावेजों की समीक्षा की जा रही है',
      doctorReviewNote: 'चिकित्सक समीक्षा के लिए तैयार सारांश',
      regenerate: 'पुनः तैयार करें',
      noData: 'अभी तक कोई नैदानिक डेटा एकत्र नहीं किया गया है। परामर्श शुरू करें या दस्तावेज़ अपलोड करें।',
      patientInfo: 'रोगी की जानकारी',
      chiefComplaint: 'मुख्य शिकायत',
      history: 'वर्तमान बीमारी का इतिहास',
      clinicalNarrative: 'चिकित्सीय विवरण',
      documents: 'दस्तावेज़ एवं जांच',
      ayushAssessment: 'आयुष मूल्यांकन',
      redFlags: 'आपातकालीन चेतावनी',
      recommendations: 'सिफारिशें एवं अगले कदम',
      patientInformation: 'मरीज़ की जानकारी',
      age: 'उम्र',
      years: 'वर्ष',
      gender: 'लिंग',
      language: 'भाषा',
      duration: 'अवधि',
      uploadedDocuments: 'अपलोड किए गए दस्तावेज़',
      conversationLog: 'परामर्श वार्तालाप लॉग',
      patientTurns: 'रोगी के कथन',
      medications: 'दवाएं',
      labResults: 'लैब परिणाम',
      parameter: 'पैरामीटर',
      value: 'मान',
      unit: 'इकाई',
      status: 'स्थिति',
      abnormal: 'असामान्य',
      normal: 'सामान्य',
      noEntities: 'इस दस्तावेज़ से कोई संरचित जानकारी नहीं मिली।',
      noSessionData: 'अभी तक कोई सत्र डेटा एकत्र नहीं हुआ है।',
      noSessionDataDesc: 'विवरण देखने के लिए बातचीत शुरू करें या दस्तावेज़ स्कैन करें।',
      done: 'पूर्ण',
      footerBrand: 'संजीवनी एआई द्वारा निर्मित — आयुष मंत्रालय · SIH 2026',
    },
  },

  bn: {
    auth: {
      title: 'ABHA দিয়ে সাইন ইন করুন',
      subtitle: 'আয়ুষ্মান ভারত হেলথ অ্যাকাউন্ট ও ওটিপি প্রমাণীকরণ',
      doctorTitle: 'চিকিৎসক পোর্টাল • HP ID দিয়ে সাইন ইন করুন',
      doctorSubtitle: 'ন্যাশনাল হেলথকেয়ার প্রফেশনালস রেজিস্ট্রি (HPR) ও ওটিপি প্রমাণীকরণ',
      patient: 'রোগী',
      doctor: 'চিকিৎসক',
      patientRoleTag: 'রোগী (Patient)',
      doctorRoleTag: 'চিকিৎসক (Doctor)',
      patientAbhaLabel: 'রোগীর ABHA আইডি / স্বাস্থ্য কার্ড নম্বর',
      doctorAbhaLabel: 'HP ID (স্বাস্থ্য পেশাদার আইডি)',
      abhaFormat: 'সাধারণ ১৪-সংখ্যার জাতীয় পরিচয়পত্র ফরম্যাট (যেমন 14-2345-6789-1011)',
      doctorFormat: 'ফরম্যাট: HP-[State]-[Number] (যেমন HP-MH-84729)',
      requestOtp: 'ABHA ওটিপি পাঠান',
      doctorRequestOtp: 'HP OTP পাঠান',
      sendingOtp: 'ওটিপি পাঠানো হচ্ছে...',
      welcome: 'স্বাগতম',
      otpSent: 'ABHA ওটিপি পাঠানো হয়েছে',
      doctorOtpSent: 'চিকিৎসক ওটিপি পাঠানো হয়েছে',
      changeAbha: 'ABHA পরিবর্তন করুন',
      changeHpId: 'HP ID পরিবর্তন করুন',
      sentToMobile: 'নিবন্ধিত মোবাইলে পাঠানো হয়েছে',
      enterOtp: '৬ সংখ্যার ওটিপি লিখুন',
      verifyAndEnter: 'যাচাই করে প্রবেশ করুন',
      verifying: 'যাচাই করা হচ্ছে...',
      register: 'নিবন্ধন',
      registerPatientPrompt: 'আপনার কি এখনও ABHA ID নেই?',
      registerPatientDesc: 'ডিজিটাল স্বাস্থ্য কার্ড তৈরি করতে অবিলম্বে নিবন্ধন করুন।',
      registerPatientButton: 'নিবন্ধন করুন ও ABHA ID তৈরি করুন',
      registerDoctorPrompt: 'আপনি কি এখনও HPR-এ নিবন্ধিত নন?',
      registerDoctorDesc: 'ক্লিনিকাল পোর্টাল অ্যাক্সেস করতে স্বাস্থ্যসেবা পেশাদার হিসাবে নিবন্ধন করুন।',
      registerDoctorButton: 'স্বাস্থ্য পেশাদার (HP ID) নিবন্ধন করুন',
      footer: 'সঞ্জীবনী ক্লিনিকাল এআই • ভারতীয় স্বাস্থ্যসেবা ও আয়ুষ কেন্দ্রের জন্য নির্মিত',
    },
    header: {
      dashboard: 'ড্যাশবোর্ড',
      profile: 'প্রোফাইল',
      viewSummary: 'সারাংশ দেখুন',
      submitDetails: 'সংরক্ষণ করুন',
      saving: 'সংরক্ষণ হচ্ছে...',
      walkInPatient: 'সাধারণ রোগী',
      abhaLinked: 'ABHA সংযুক্ত',
      changeLanguage: 'ভাষা পরিবর্তন',
    },
    dashboard: {
      abhaLinked: 'ABHA সংযুক্ত',
      profile: 'প্রোফাইল',
      addDetails: 'লক্ষণ যোগ করুন',
      signOut: 'লগ আউট',
      personalDetails: 'ব্যক্তিগত বিবরণ',
      activeMedications: 'চলতি ওষুধপত্র',
      pastMedications: 'আগের ওষুধপত্র',
      recentIntakeSessions: 'সাম্প্রতিক ক্লিনিকাল সেশন',
      medicalDocuments: 'চিকিৎসা নথি',
      startIntakeConsultation: 'নতুন পরামর্শ শুরু করুন',
      noRecords: 'কোনো তথ্য নেই',
      allergies: 'অ্যালার্জি',
      chronicConditions: 'দীর্ঘস্থায়ী রোগ',
      emergencyContact: 'জরুরি যোগাযোগ',
      noAllergies: 'ওষুধের কোনো অ্যালার্জি জানা নেই',
      noChronic: 'কোনোটি নেই',
      viewSummary: 'সারাংশ দেখুন',
    },
    chat: {
      clinicalIntake: 'ক্লিনিকাল ইনটেক পরামর্শ',
      chatCompleted: 'পরামর্শ সম্পন্ন',
      inputPlaceholder: 'আপনার ভাষায় আপনার লক্ষণগুলি বলুন বা লিখুন...',
      send: 'পাঠান',
      listening: 'শুনছি...',
      speak: 'ভয়েস ইনপুট',
      suggestedQuickReplies: 'প্রস্তাবিত উত্তর',
      finishChat: 'পরামর্শ শেষ করুন',
      continueChat: 'পরামর্শ চালিয়ে যান',
      restartChat: 'পুনরায় শুরু করুন',
      chatEndedTitle: 'পরামর্শ শেষ হয়েছে',
      chatEndedDesc: 'আপনার স্বাস্থ্য লক্ষণ লিপিবদ্ধ করা হয়েছে। আপনি সারাংশ দেখতে বা সংরক্ষণ করতে পারেন।',
      generatingQuestion: 'প্রশ্ন তৈরি হচ্ছে...',
      endIntentPrompt: 'আপনি কি কথোপকথন শেষ করতে চান?',
      endIntentYes: 'হ্যাঁ, কথোপকথন শেষ করুন',
      endIntentNo: 'না',
    },
    scanner: {
      title: 'মেডিকেল ডকুমেন্ট স্ক্যানার',
      uploaded: 'আপলোড হয়েছে',
      takePhoto: 'ছবি তুলুন',
      uploadFile: 'ফাইল আপলোড',
      dropzone: 'প্রেসক্রিপশন বা রিপোর্ট ছবি এখানে আনুন বা ক্লিক করুন',
      scanning: 'নথি স্ক্যান ও ডিজিটাইজ হচ্ছে...',
    },
    doctor: {
      hprVerified: 'HPR যাচাইকৃত চিকিৎসক',
      signOut: 'সাইন আউট',
      stationTitle: 'চিকিৎসক ক্লিনিকাল তত্ত্বাবধান স্টেশন (EHR ডেটাবেস)',
      clinicalReviewTitle: 'ক্লিনিকাল পর্যালোচনা ও রোগী রেকর্ড রেজিস্ট্রি',
      portalDescription: 'ডেটাবেসে সমস্ত নিবন্ধিত রোগীর সাথে সংযুক্ত স্বাস্থ্য পরিষেবা পোর্টাল। AI ডিজিটালাইজড প্রতিলিপি এবং প্রেসক্রিপশন পর্যালোচনা করুন।',
      opdRegular: 'ওপিডি নিয়মিত',
      dutyActive: 'ডিউটি: সক্রিয়',
      today: 'আজ',
      registeredPatients: 'নিবন্ধিত রোগী',
      liveRecords: 'লাইভ ডেটাবেস রেকর্ড',
      triageRedFlags: 'জরুরি রেড ফ্ল্যাগ',
      urgentAttention: 'জরুরি মনোযোগ প্রয়োজন',
      vitalsStable: 'সমস্ত লক্ষণ স্থিতিশীল',
      digitizedPrescriptions: 'ডিজিটাইজড প্রেসক্রিপশন',
      verifiedOcr: 'AI ভিশন OCR দ্বারা যাচাইকৃত',
      consultationSessions: 'পরামর্শ সেশন',
      aiIntakeConsultations: 'AI প্রাথমিক পরামর্শ',
      searchPlaceholder: 'নাম বা ABHA ID দিয়ে রোগী খুঁজুন...',
      redFlagsOnly: 'শুধুমাত্র রেড ফ্ল্যাগ',
      withDocuments: 'নথিপত্র সহ',
      refreshRecords: 'রোগীর রেকর্ড রিফ্রেশ করুন',
      patientQueue: 'লাইভ রোগী পর্যালোচনা সারি',
      colPatient: 'রোগী',
      colDemographics: 'জনপরিসংখ্যান',
      colTriage: 'ট্রায়াজ অবস্থা',
      colDocuments: 'নথিপত্র',
      colActions: 'পদক্ষেপ',
      noPatients: 'কোনো রোগী পাওয়া যায়নি।',
      reviewDossier: 'ডোসিয়ার পর্যালোচনা',
      tabConsultations: 'AI परामर्श',
      tabPrescriptions: 'প্রেসক্রিপশন',
      tabMedications: 'ওষুধ',
      tabBaseline: 'আয়ুষ ও বেসলাইন',
      closeDossier: 'ডোসিয়ার বন্ধ করুন',
      patientLanguage: 'রোগীর ভাষা',
      translateTo: 'অনুবাদ করুন',
      translating: 'অনুবাদ হচ্ছে...',
      showOriginal: 'মূল রূপ',
      showTranslated: 'অনূদিত',
      translatedBadge: 'AI ক্লিনিকাল অনুবাদ',
      differentLanguageNotice: 'এই পরামর্শটি অন্য ভাষায় নথিভুক্ত করা হয়েছিল।',
      translationUnavailable: 'অনুবাদ অনুপলব্ধ',
      selectLanguage: 'ভাষা নির্বাচন করুন',
    },
    summary: {
      title: 'ক্লিনিকাল সেশনের সারাংশ',
      subtitle: 'চিকিৎসকের পর্যালোচনার জন্য প্রস্তুত',
      print: 'প্রিন্ট করুন',
      close: 'সারাংশ বন্ধ করুন',
      aiTab: 'এআই সারাংশ',
      detailsTab: 'বিস্তারিত বিবরণ',
      generatePromptTitle: 'এআই সারাংশ তৈরি করুন',
      generatePromptDesc: 'সঞ্জীবনী এআই আপনার কথোপকথনের ইতিহাস এবং আপলোড করা নথিগুলি চিকিৎসকের জন্য একটি কাঠামোগত সারাংশে সংশ্লেষিত করবে।',
      generateButton: 'সারাংশ তৈরি করুন',
      loadingTitle: 'সঞ্জীবনী এআই আপনার ক্লিনিকাল সারাংশ প্রস্তুত করছে…',
      loadingDesc: 'কথোপকথন এবং স্ক্যান করা নথি পর্যালোচনা করা হচ্ছে',
      doctorReviewNote: 'ডাক্তারের পর্যালোচনার জন্য প্রস্তুত সারাংশ',
      regenerate: 'পুনরায় তৈরি করুন',
      noData: 'এখনও কোনো চিকিৎসার তথ্য সংগ্রহ করা হয়নি। পরামর্শ শুরু করুন বা নথি আপলোড করুন।',
      patientInfo: 'রোগীর তথ্য',
      chiefComplaint: 'প্রধান সমস্যা',
      history: 'বর্তমান অসুস্থতার ইতিহাস',
      clinicalNarrative: 'চিকিৎসা সংক্রান্ত বিবরণ',
      documents: 'নথিপত্র ও পরীক্ষা নিরীক্ষা',
      ayushAssessment: 'আয়ুষ মূল্যায়ন',
      redFlags: 'জরুরি সতর্কতা',
      recommendations: 'পরামর্শ ও পরবর্তী পদক্ষেপ',
      patientInformation: 'রোগীর পরিচয়',
      age: 'বয়স',
      years: 'বছর',
      gender: 'লিঙ্গ',
      language: 'ভাষা',
      duration: 'সময়কাল',
      uploadedDocuments: 'আপলোড করা নথি',
      conversationLog: 'কথোপকথন লগ',
      patientTurns: 'রোগীর বক্তব্য',
      medications: 'ওষুধের তালিকা',
      labResults: 'ল্যাব পরীক্ষার ফলাফল',
      parameter: 'প্যারামিটার',
      value: 'মান',
      unit: 'একক',
      status: 'অবস্থা',
      abnormal: 'অস্বাভাবিক',
      normal: 'স্বাভাবিক',
      noEntities: 'এই নথি থেকে কোনো কাঠামোগত তথ্য পাওয়া যায়নি।',
      noSessionData: 'এখনও পর্যন্ত কোনো সেশন ডেটা সংগৃহীত হয়নি।',
      noSessionDataDesc: 'এখানে বিশদ দেখতে চ্যাট শুরু করুন বা একটি নথি স্ক্যান করুন।',
      done: 'সম্পন্ন',
      footerBrand: 'সঞ্জীবনী এআই দ্বারা জেনারেট করা হয়েছে — আয়ুষ মন্ত্রক · SIH 2026',
    },
  },

  ta: {
    auth: {
      title: 'ABHA மூலம் உள்நுழைக',
      subtitle: 'ஆயுஷ்மான் பாரத் சுகாதார கணக்கு மற்றும் OTP அங்கீகாரம்',
      doctorTitle: 'மருத்துவர் தளம் • HP ID மூலம் உள்நுழைக',
      doctorSubtitle: 'தேசிய சுகாதார வல்லுநர் பதிவு (HPR) & OTP அங்கீகாரம்',
      patient: 'நோயாளி',
      doctor: 'மருத்துவர்',
      patientRoleTag: 'நோயாளி (Patient)',
      doctorRoleTag: 'மருத்துவர் (Doctor)',
      patientAbhaLabel: 'நோயாளி ABHA எண் / சுகாதார அட்டை எண்',
      doctorAbhaLabel: 'HP ID (சுகாதார வல்லுநர் அடையாள எண்)',
      abhaFormat: 'நிலையான 14-இலக்க தேசிய அடையாள வடிவம் (எ.கா. 14-2345-6789-1011)',
      doctorFormat: 'வடிவம்: HP-[மாநிலம்]-[எண்] (எ.கா. HP-MH-84729)',
      requestOtp: 'ABHA OTP கோருக',
      doctorRequestOtp: 'HP OTP கோருக',
      sendingOtp: 'OTP அனுப்பப்படுகிறது...',
      welcome: 'வரவேற்கிறோம்',
      otpSent: 'ABHA OTP அனுப்பப்பட்டது',
      doctorOtpSent: 'மருத்துவர் OTP அனுப்பப்பட்டது',
      changeAbha: 'ABHA மாற்றுக',
      changeHpId: 'HP ID மாற்றுக',
      sentToMobile: 'பதிவுசெய்த மொபைலுக்கு அனுப்பப்பட்டது',
      enterOtp: '6-இலக்க OTP உள்ளிடுக',
      verifyAndEnter: 'சரிபார்த்து நுழைக',
      verifying: 'சரிபார்க்கிறது...',
      register: 'பதிவு செய்க',
      registerPatientPrompt: 'இன்னும் உங்களிடம் ABHA ID இல்லையா?',
      registerPatientDesc: 'டிஜிட்டல் சுகாதார அட்டையை பெற உடனே பதிவு செய்யவும்.',
      registerPatientButton: 'பதிவு செய்து ABHA ID பெறுக',
      registerDoctorPrompt: 'HPR-ல் இன்னும் பதிவு செய்யவில்லையா?',
      registerDoctorDesc: 'மருத்துவ தளத்தை அணுக சுகாதார வல்லுநராக பதிவு செய்யவும்.',
      registerDoctorButton: 'சுகாதார வல்லுநர் (HP ID) பதிவு செய்க',
      footer: 'சஞ்சீவனி மருத்துவ AI • இந்திய சுகாதார மையங்கள் மற்றும் ஆயுஷ் மையங்களுக்காக உருவாக்கப்பட்டது',
    },
    header: {
      dashboard: 'முகப்பு பலகை',
      profile: 'சுயவிவரம்',
      viewSummary: 'சுருக்கம் காண்க',
      submitDetails: 'விவரங்களைச் சேமி',
      saving: 'சேமிக்கப்படுகிறது...',
      walkInPatient: 'நோயாளி',
      abhaLinked: 'ABHA இணைக்கப்பட்டது',
      changeLanguage: 'மொழியை மாற்றுக',
    },
    dashboard: {
      abhaLinked: 'ABHA இணைக்கப்பட்டது',
      profile: 'சுயவிவரம்',
      addDetails: 'அறிகுறிகளைச் சேர்க்க',
      signOut: 'வெளியேறு',
      personalDetails: 'தனிப்பட்ட விவரங்கள்',
      activeMedications: 'தற்போதைய மருந்துகள்',
      pastMedications: 'முந்தைய மருந்துகள்',
      recentIntakeSessions: 'சமீபத்திய மருத்துவ ஆலோசனைகள்',
      medicalDocuments: 'மருத்துவ ஆவணங்கள்',
      startIntakeConsultation: 'ஆலோசனையைத் தொடங்கு',
      noRecords: 'பதிவுகள் எதுவும் இல்லை',
      allergies: 'ஒவ்வாமை',
      chronicConditions: 'நீண்டகால நோய்கள்',
      emergencyContact: 'அவசர தொடர்பு',
      noAllergies: 'மருந்து ஒவ்வாமை எதுவும் தெரிவிக்கப்படவில்லை',
      noChronic: 'எதுவும் இல்லை',
      viewSummary: 'சுருக்கம் காண்க',
    },
    chat: {
      clinicalIntake: 'மருத்துவ ஆலோசனை மற்றும் அறிகுறிகள்',
      chatCompleted: 'உரையாடல் முடிந்தது',
      inputPlaceholder: 'உங்கள் அறிகுறிகளை உங்கள் மொழியில் பேசவும் அல்லது தட்டச்சு செய்யவும்...',
      send: 'அனுப்புக',
      listening: 'கேட்கிறது...',
      speak: 'குரல் உள்ளீடு',
      suggestedQuickReplies: 'பரிந்துரைக்கப்பட்ட பதில்கள்',
      finishChat: 'ஆலோசனையை முடிக்க',
      continueChat: 'தொடர்க',
      restartChat: 'மீண்டும் தொடங்க',
      chatEndedTitle: 'மருத்துவ ஆலோசனை முடிவடைந்தது',
      chatEndedDesc: 'உங்கள் அறிகுறிகள் மற்றும் மருத்துவ விவரங்கள் ஆவணப்படுத்தப்பட்டுள்ளன.',
      generatingQuestion: 'கேள்வி உருவாக்கப்படுகிறது...',
      endIntentPrompt: 'உரையாடலை முடிக்க விரும்புகிறீர்களா?',
      endIntentYes: 'ஆம், உரையாடலை முடிக்கவும்',
      endIntentNo: 'இல்லை',
    },
    scanner: {
      title: 'மருத்துவ ஆவண ஸ்கேனர்',
      uploaded: 'பதிவேற்றப்பட்டது',
      takePhoto: 'புகைப்படம் எடு',
      uploadFile: 'கோப்பைப் பதிவேற்று',
      dropzone: 'மருந்து சீட்டு அல்லது அறிக்கை படத்தை இங்கே இழுத்து விடவும்',
      scanning: 'ஆவணம் ஸ்கேன் செய்யப்படுகிறது...',
    },
    doctor: {
      hprVerified: 'HPR சரிபார்க்கப்பட்ட மருத்துவர்',
      signOut: 'வெளியேறு',
      stationTitle: 'மருத்துவர் மருத்துவ மேற்பார்வை நிலையம் (EHR தரவுத்தளம்)',
      clinicalReviewTitle: 'மருத்துவ ஆய்வு மற்றும் நோயாளி பதிவேடு',
      portalDescription: 'பதிவுசெய்யப்பட்ட அனைத்து நோயாளிகளுடனும் இணைக்கப்பட்ட சுகாதார வழங்குநர் தளம். AI டிஜிட்டல் ஆவணங்களை ஆய்வு செய்யவும்.',
      opdRegular: 'OPD வழக்கமான',
      dutyActive: 'பணி: செயலில்',
      today: 'இன்று',
      registeredPatients: 'பதிவுசெய்யப்பட்ட நோயாளிகள்',
      liveRecords: 'நேரடி தரவுத்தள பதிவுகள்',
      triageRedFlags: 'அவசர எச்சரிக்கை குறிகள்',
      urgentAttention: 'அவசர கவனம் தேவை',
      vitalsStable: 'அனைத்து அளவுகளும் சீராக உள்ளன',
      digitizedPrescriptions: 'டிஜிட்டல் மருந்துச்சீட்டுகள்',
      verifiedOcr: 'AI விஷன் OCR மூலம் சரிபார்க்கப்பட்டது',
      consultationSessions: 'ஆலோசனை அமர்வுகள்',
      aiIntakeConsultations: 'AI உட்கொள்ளல் ஆலோசனைகள்',
      searchPlaceholder: 'பெயர் அல்லது ABHA ID மூலம் நோயாளியைத் தேடுங்கள்...',
      redFlagsOnly: 'எச்சரிக்கைக் குறிகள் மட்டும்',
      withDocuments: 'ஆவணங்களுடன்',
      refreshRecords: 'பதிவுகளைப் புதுப்பிக்கவும்',
      patientQueue: 'நேரடி நோயாளி வரிசை',
      colPatient: 'நோயாளி',
      colDemographics: 'மக்கள்தொகை விவரம்',
      colTriage: 'நிலை வகைப்படுத்தல்',
      colDocuments: 'ஆவணங்கள்',
      colActions: 'செயல்கள்',
      noPatients: 'நோயாளிகள் எவரும் காணப்படவில்லை.',
      reviewDossier: 'ஆவணத்தொகுப்பை மதிப்பாய்வு செய்',
      tabConsultations: 'AI ஆலோசனைகள்',
      tabPrescriptions: 'மருந்துச்சீட்டுகள்',
      tabMedications: 'மருந்துகள்',
      tabBaseline: 'ஆயுஷ் & அடிப்படை',
      closeDossier: 'மூடுக',
      patientLanguage: 'நோயாளி மொழி',
      translateTo: 'மொழிபெயர்',
      translating: 'மொழிபெயர்க்கப்படுகிறது...',
      showOriginal: 'அசல்',
      showTranslated: 'மொழிபெயர்க்கப்பட்டது',
      translatedBadge: 'AI மருத்துவ மொழிபெயர்ப்பு',
      differentLanguageNotice: 'இந்த ஆலோசனை வேறு மொழியில் பதிவு செய்யப்பட்டது.',
      translationUnavailable: 'மொழிபெயர்ப்பு கிடைக்கவில்லை',
      selectLanguage: 'மொழியைத் தேர்ந்தெடுக்கவும்',
    },
    summary: {
      title: 'மருத்துவ அமர்வு சுருக்கம்',
      subtitle: 'மருத்துவர் பார்வைக்கு தயார்',
      print: 'அச்சிடுக',
      close: 'சுருக்கத்தை மூடுக',
      aiTab: 'AI சுருக்கம்',
      detailsTab: 'விவரங்கள்',
      generatePromptTitle: 'AI சுருக்கத்தை உருவாக்கு',
      generatePromptDesc: 'சஞ்சீவனி AI உங்கள் உரையாடல் வரலாறு மற்றும் ஆவணங்களை மருத்துவருக்கு ஏற்ற சுருக்கமாக தொகுக்கும்.',
      generateButton: 'சுருக்கத்தை உருவாக்கு',
      loadingTitle: 'சஞ்சீவனி AI மருத்துவ சுருக்கத்தைத் தயாரிக்கிறது…',
      loadingDesc: 'உரையாடல் மற்றும் ஆவணங்களை மதிப்பாய்வு செய்கிறது',
      doctorReviewNote: 'மருத்துவர் மதிப்பாய்விற்காக உருவாக்கப்பட்ட சுருக்கம்',
      regenerate: 'மீண்டும் உருவாக்கு',
      noData: 'இன்னும் மருத்துவத் தகவல்கள் சேகரிக்கப்படவில்லை. ஆலோசனையைத் தொடங்குங்கள் அல்லது ஆவணங்களைப் பதிவேற்றுங்கள்.',
      patientInfo: 'நோயாளி விவரங்கள்',
      chiefComplaint: 'முக்கிய பிரச்சனை',
      history: 'தற்போதைய நோய் வரலாறு',
      clinicalNarrative: 'மருத்துவ அறிக்கை',
      documents: 'ஆவணங்கள் மற்றும் சோதனைகள்',
      ayushAssessment: 'ஆயுஷ் மதிப்பீடு',
      redFlags: 'அவசர எச்சரிக்கை',
      recommendations: 'பரிந்துரைகள் மற்றும் அடுத்த கட்ட நடவடிக்கை',
      patientInformation: 'நோயாளி தகவல்',
      age: 'வயது',
      years: 'ஆண்டுகள்',
      gender: 'பாலினம்',
      language: 'மொழி',
      duration: 'கால அளவு',
      uploadedDocuments: 'பதிவேற்றிய ஆவணங்கள்',
      conversationLog: 'உரையாடல் பதிவு',
      patientTurns: 'நோயாளி பதில்கள்',
      medications: 'மருந்துகள்',
      labResults: 'ஆய்வக முடிவுகள்',
      parameter: 'அளவுரு',
      value: 'மதிப்பு',
      unit: 'அலகு',
      status: 'நிலை',
      abnormal: 'இயல்புக்கு மாறான',
      normal: 'இயல்பான',
      noEntities: 'இந்த ஆவணத்திலிருந்து தகவல்கள் எதுவும் எடுக்கப்படவில்லை.',
      noSessionData: 'இன்னும் எந்த அமர்வுத் தரவும் சேகரிக்கப்படவில்லை.',
      noSessionDataDesc: 'விவரங்களை இங்கே காண உரையாடலைத் தொடங்கவும் அல்லது ஆவணத்தை ஸ்கேன் செய்யவும்.',
      done: 'முடிந்தது',
      footerBrand: 'சஞ்சீவனி AI உருவாக்கியது — ஆயுஷ் அமைச்சகம் · SIH 2026',
    },
  },

  te: {
    auth: {
      title: 'ABHA తో సైన్ ఇన్ చేయండి',
      subtitle: 'ఆయుష్మాన్ భారత్ హెల్త్ అకౌంట్ & OTP ప్రమాణీకరణ',
      doctorTitle: 'వైద్యుల పోర్టల్ • HP ID తో సైన్ ఇన్ చేయండి',
      doctorSubtitle: 'నేషనల్ హెల్త్‌కేర్ ప్రొఫెషనల్స్ రిజిస్ట్రీ (HPR) & OTP ప్రమాణీకరణ',
      patient: 'రోగి',
      doctor: 'వైద్యుడు',
      patientRoleTag: 'రోగి (Patient)',
      doctorRoleTag: 'వైద్యుడు (Doctor)',
      patientAbhaLabel: 'రోగి ABHA ID / హెల్త్ కార్డ్ నంబర్',
      doctorAbhaLabel: 'HP ID (ఆరోగ్య నిపుణుల గుర్తింపు)',
      abhaFormat: 'ప్రామాణిక 14 అంకెల జాతీయ గుర్తింపు ఫార్మాట్ (ఉదా. 14-2345-6789-1011)',
      doctorFormat: 'ఫార్మాట్: HP-[రాష్ట్రం]-[సంఖ్య] (ఉదా. HP-MH-84729)',
      requestOtp: 'ABHA OTP పంపండి',
      doctorRequestOtp: 'HP OTP పంపండి',
      sendingOtp: 'OTP పంపుతోంది...',
      welcome: 'స్వాగతం',
      otpSent: 'ABHA OTP పంపబడింది',
      doctorOtpSent: 'వైద్యుల OTP పంపబడింది',
      changeAbha: 'ABHA మార్చండి',
      changeHpId: 'HP ID మార్చండి',
      sentToMobile: 'నమోదిత మొబైల్‌కు పంపబడింది',
      enterOtp: '6 అంకెల OTP నమోదు చేయండి',
      verifyAndEnter: 'ధృవీకరించి ప్రవేశించండి',
      verifying: 'ధృవీకరిస్తోంది...',
      register: 'నమోదు చేసుకోండి',
      registerPatientPrompt: 'మీకు ఇంకా ABHA ID లేదా?',
      registerPatientDesc: 'డిజిటల్ హెల్త్ కార్డు పొందడానికి వెంటనే నమోదు చేసుకోండి.',
      registerPatientButton: 'నమోదు చేసుకొని ABHA ID పొందండి',
      registerDoctorPrompt: 'HPR లో ఇంకా నమోదు కాలేదా?',
      registerDoctorDesc: 'క్లినికల్ పోర్టల్ యాక్సెస్ చేయడానికి హెల్త్‌కేర్ ప్రొఫెషనల్‌గా నమోదు చేసుకోండి.',
      registerDoctorButton: 'హెల్త్‌కేర్ ప్రొఫెషనల్ (HP ID) నమోదు',
      footer: 'సంజీవని క్లినికల్ AI • భారతీయ ఆరోగ్య కేంద్రాలు & ఆయుష్ కేంద్రాల కోసం రూపొందించబడింది',
    },
    header: {
      dashboard: 'డ్యాష్‌బోర్డ్',
      profile: 'ప్రొఫైల్',
      viewSummary: 'సారాంశం చూడండి',
      submitDetails: 'వివరాలు సేవ్ చేయండి',
      saving: 'సేవ్ చేస్తోంది...',
      walkInPatient: 'రోగి',
      abhaLinked: 'ABHA లింక్ చేయబడింది',
      changeLanguage: 'భాష మార్చండి',
    },
    dashboard: {
      abhaLinked: 'ABHA లింక్ చేయబడింది',
      profile: 'ప్రొఫైల్',
      addDetails: 'లక్షణాలు జోడించండి',
      signOut: 'లాగ్ అవుట్',
      personalDetails: 'వ్యక్తిగత వివరాలు',
      activeMedications: 'ప్రస్తుత మందులు',
      pastMedications: 'గత మందులు',
      recentIntakeSessions: 'ఇటీవలి సంప్రదింపులు',
      medicalDocuments: 'వైద్య పత్రాలు',
      startIntakeConsultation: 'కొత్త సంప్రదింపు ప్రారంభించండి',
      noRecords: 'రికార్డులు కనుగొనబడలేదు',
      allergies: 'అలెర్జీలు',
      chronicConditions: 'దీర్ఘకాలిక సమస్యలు',
      emergencyContact: 'అత్యవసర పరిచయం',
      noAllergies: 'మందుల అలెర్జీలు లేవు',
      noChronic: 'ఏమీ నమోదు కాలేదు',
      viewSummary: 'సారాంశం చూడండి',
    },
    chat: {
      clinicalIntake: 'క్లినికల్ సంప్రదింపులు',
      chatCompleted: 'సంభాషణ ముగిసింది',
      inputPlaceholder: 'మీ భాషలో మాట్లాడండి లేదా మీ లక్షణాలను టైప్ చేయండి...',
      send: 'పంపు',
      listening: 'వింటోంది...',
      speak: 'వాయిస్ ఇన్‌పుట్',
      suggestedQuickReplies: 'సూచించిన సమాధానాలు',
      finishChat: 'సంప్రదింపు ముగించండి',
      continueChat: 'కొనసాగించండి',
      restartChat: 'మళ్లీ ప్రారంభించండి',
      chatEndedTitle: 'సంప్రదింపు ముగిసింది',
      chatEndedDesc: 'మీ ఆరోగ్య లక్షణాలు నమోదు చేయబడ్డాయి.',
      generatingQuestion: 'ప్రశ్న సిద్ధం అవుతోంది...',
      endIntentPrompt: 'మీరు చాట్ ముగించాలనుకుంటున్నారా?',
      endIntentYes: 'అవును, చాట్ ముగించు',
      endIntentNo: 'కాదు',
    },
    scanner: {
      title: 'మెడికల్ డాక్యుమెంట్ స్కానర్',
      uploaded: 'అప్‌లోడ్ చేయబడింది',
      takePhoto: 'ఫోటో తీయండి',
      uploadFile: 'ఫైల్ ఎంచుకోండి',
      dropzone: 'ప్రిస్క్రిప్షన్ లేదా ల్యాబ్ రిపోర్ట్ చిత్రాన్ని ఇక్కడ ఉంచండి',
      scanning: 'పత్రం స్కాన్ అవుతోంది...',
    },
    doctor: {
      hprVerified: 'HPR ధృవీకరించబడిన వైద్యులు',
      signOut: 'లాగ్ అవుట్',
      stationTitle: 'వైద్యుల క్లినికల్ పర్యవేక్షణ స్టేషన్ (EHR డేటాబేస్)',
      clinicalReviewTitle: 'క్లినికల్ సమీక్ష & రోగి రికార్డుల రిజిస్ట్రీ',
      portalDescription: 'నమోదైన రోగులందరికీ అనుసంధానించబడిన ఆరోగ్య సేవల పోర్టల్. AI డిజిటలైజ్డ్ ప్రిస్క్రిప్షన్లను సమీక్షించండి.',
      opdRegular: 'OPD రెగ్యులర్',
      dutyActive: 'డ్యూటీ: యాక్టివ్',
      today: 'ఈ రోజు',
      registeredPatients: 'నమోదైన రోగులు',
      liveRecords: 'ప్రత్యక్ష డేటాబేస్ రికార్డులు',
      triageRedFlags: 'అత్యవసర రెడ్ ఫ్లాగ్‌లు',
      urgentAttention: 'తక్షణ శ్రద్ధ అవసరం',
      vitalsStable: 'అన్ని సంకేతాలు స్థిరంగా ఉన్నాయి',
      digitizedPrescriptions: 'డిజిటలైజ్డ్ ప్రిస్క్రిప్షన్‌లు',
      verifiedOcr: 'AI విజన్ OCR ద్వారా ధృవీకరించబడింది',
      consultationSessions: 'సంప్రదింపు సెషన్‌లు',
      aiIntakeConsultations: 'AI ప్రాథమిక సంప్రదింపులు',
      searchPlaceholder: 'పేరు లేదా ABHA ID ద్వారా రోగులను శోధించండి...',
      redFlagsOnly: 'రెడ్ ఫ్లాగ్‌లు మాత్రమే',
      withDocuments: 'పత్రాలతో కూడినవి',
      refreshRecords: 'రోగి రికార్డులను రిఫ్రెష్ చేయండి',
      patientQueue: 'ప్రత్యక్ష రోగి సమీక్ష వరుస',
      colPatient: 'రోగి',
      colDemographics: 'జనాభా వివరాలు',
      colTriage: 'ట్రయాజ్ స్థితి',
      colDocuments: 'పత్రాలు',
      colActions: 'చర్యలు',
      noPatients: 'రోగులెవరూ కనుగొనబడలేదు.',
      reviewDossier: 'డోసియర్ సమీక్షించండి',
      tabConsultations: 'AI సంప్రదింపులు',
      tabPrescriptions: 'ప్రిస్క్రిప్షన్లు',
      tabMedications: 'మందులు',
      tabBaseline: 'ఆయుష్ & బేస్‌లైన్',
      closeDossier: 'డోసియర్ మూసివేయి',
      patientLanguage: 'రోగి భాష',
      translateTo: 'అనువదించు',
      translating: 'అనువదిస్తోంది...',
      showOriginal: 'అసలు',
      showTranslated: 'అనువాదం',
      translatedBadge: 'AI క్లినికల్ అనువాదం',
      differentLanguageNotice: 'ఈ సంప్రదింపు వేరే భాషలో నమోదు చేయబడింది.',
      translationUnavailable: 'అనువాదం అందుబాటులో లేదు',
      selectLanguage: 'భాషను ఎంచుకోండి',
    },
    summary: {
      title: 'క్లినికల్ సెషన్ సారాంశం',
      subtitle: 'వైద్యుల సమీక్ష కోసం సిద్ధంగా ఉంది',
      print: 'ప్రింట్ చేయండి',
      close: 'సారాంశాన్ని మూసివేయండి',
      aiTab: 'AI సారాంశం',
      detailsTab: 'వివరాలు',
      generatePromptTitle: 'AI సారాంశాన్ని రూపొందించండి',
      generatePromptDesc: 'సంజీవని AI మీ సంభాషణ మరియు అప్‌లోడ్ చేసిన పత్రాలను వైద్యుల కోసం ఒక నిర్మాణాత్మక సారాంశంగా రూపొందిస్తుంది.',
      generateButton: 'సారాంశాన్ని రూపొందించండి',
      loadingTitle: 'సంజీవని AI మీ వైద్య సారాంశాన్ని సిద్ధం చేస్తోంది…',
      loadingDesc: 'సంభాషణ మరియు స్కాన్ చేసిన పత్రాలను సమీక్షిస్తోంది',
      doctorReviewNote: 'వైద్యుల సమీక్ష కోసం రూపొందించబడిన సారాంశం',
      regenerate: 'మళ్లీ రూపొందించండి',
      noData: 'ఇంకా ఎలాంటి వైద్య సమాచారం సేకరించబడలేదు. సంప్రదింపులను ప్రారంభించండి లేదా పత్రాలను అప్‌లోడ్ చేయండి.',
      patientInfo: 'రోగి సమాచారం',
      chiefComplaint: 'ప్రధాన సమస్య',
      history: 'ప్రస్తుత అనారోగ్య చరిత్ర',
      clinicalNarrative: 'వైద్య వివరణ',
      documents: 'పత్రాలు మరియు పరీక్షలు',
      ayushAssessment: 'ఆయుష్ అంచనా',
      redFlags: 'అత్యవసర హెచ్చరిక',
      recommendations: 'సిఫార్సులు మరియు తదుపరి చర్యలు',
      patientInformation: 'రోగి వివరాలు',
      age: 'వయస్సు',
      years: 'సంవత్సరాలు',
      gender: 'లింగం',
      language: 'భాష',
      duration: 'వ్యవధి',
      uploadedDocuments: 'అప్‌లోడ్ చేసిన పత్రాలు',
      conversationLog: 'సంభాషణ లాగ్',
      patientTurns: 'రోగి సంభాషణలు',
      medications: 'మందులు',
      labResults: 'ల్యాబ్ ఫలితాలు',
      parameter: 'పరామితి',
      value: 'విలువ',
      unit: 'ప్రమాణం',
      status: 'స్థితి',
      abnormal: 'అసాధారణ',
      normal: 'సాధారణ',
      noEntities: 'ఈ పత్రం నుండి ఎలాంటి సమాచారం లభించలేదు.',
      noSessionData: 'ఇంకా ఎటువంటి సెషన్ డేటా సేకరించబడలేదు.',
      noSessionDataDesc: 'వివరాలను ఇక్కడ చూడటానికి సంభాషణ ప్రారంభించండి లేదా పత్రాన్ని స్కాన్ చేయండి.',
      done: 'పూర్తయింది',
      footerBrand: 'సంజీవని AI ద్వారా రూపొందించబడింది — ఆయుష్ మంత్రిత్వ శాఖ · SIH 2026',
    },
  },

  mr: {
    auth: {
      title: 'आभा (ABHA) सह साइन इन करा',
      subtitle: 'आयुष्मान भारत आरोग्य खाते आणि OTP प्रमाणीकरण',
      doctorTitle: 'डॉक्टर पोर्टल • HP ID सह साइन इन करा',
      doctorSubtitle: 'राष्ट्रीय आरोग्य व्यावसायिक नोंदणी (HPR) आणि OTP प्रमाणीकरण',
      patient: 'रुग्ण',
      doctor: 'डॉक्टर',
      patientRoleTag: 'रुग्ण (Patient)',
      doctorRoleTag: 'डॉक्टर (Doctor)',
      patientAbhaLabel: 'रुग्ण ABHA आयडी / आरोग्य कार्ड क्रमांक',
      doctorAbhaLabel: 'HP ID (आरोग्य व्यावसायिक आयडी)',
      abhaFormat: 'मानक १४-अंकी राष्ट्रीय ओळख स्वरूप (उदा. 14-2345-6789-1011)',
      doctorFormat: 'स्वरूप: HP-[राज्य]-[संख्या] (उदा. HP-MH-84729)',
      requestOtp: 'ABHA OTP मागवा',
      doctorRequestOtp: 'HP OTP मागवा',
      sendingOtp: 'OTP पाठवला जात आहे...',
      welcome: 'स्वागत आहे',
      otpSent: 'ABHA OTP पाठवला गेला आहे',
      doctorOtpSent: 'डॉक्टर OTP पाठवला गेला आहे',
      changeAbha: 'ABHA बदला',
      changeHpId: 'HP ID बदला',
      sentToMobile: 'नोंदणीकृत मोबाईलवर पाठवले',
      enterOtp: '६-अंकी OTP टाका',
      verifyAndEnter: 'सत्यापित करून प्रवेश करा',
      verifying: 'सत्यापन सुरू आहे...',
      register: 'नोंदणी करा',
      registerPatientPrompt: 'तुमच्याकडे अद्याप ABHA ID नाही का?',
      registerPatientDesc: 'डिजिटल आरोग्य कार्ड तयार करण्यासाठी त्वरित नोंदणी करा.',
      registerPatientButton: 'नोंदणी करा आणि ABHA ID मिळवा',
      registerDoctorPrompt: 'HPR वर अद्याप नोंदणी झालेली नाही का?',
      registerDoctorDesc: 'क्लिनिकल पोर्टलवर प्रवेश करण्यासाठी आरोग्य व्यावसायिक म्हणून नोंदणी करा.',
      registerDoctorButton: 'आरोग्य व्यावसायिक (HP ID) नोंदणी करा',
      footer: 'संजीवनी क्लिनिकल एआय • भारतीय आरोग्य सुविधा आणि आयुष केंद्रांसाठी विकसित',
    },
    header: {
      dashboard: 'डॅशबोर्ड',
      profile: 'प्रोफाइल',
      viewSummary: 'सारांश पहा',
      submitDetails: 'माहिती सेव्ह करा',
      saving: 'सेव्ह होत आहे...',
      walkInPatient: 'रुग्ण',
      abhaLinked: 'ABHA जोडले आहे',
      changeLanguage: 'भाषा बदला',
    },
    dashboard: {
      abhaLinked: 'ABHA जोडले आहे',
      profile: 'प्रोफाइल',
      addDetails: 'तपशील जोडा',
      signOut: 'साइन आउट',
      personalDetails: 'वैयक्तिक माहिती',
      activeMedications: 'सध्याची औषधे',
      pastMedications: 'मागील औषधे',
      recentIntakeSessions: 'अलीकडील सल्लामसलत सत्रे',
      medicalDocuments: 'वैद्यकीय कागदपत्रे',
      startIntakeConsultation: 'नवीन सल्लामसलत सुरू करा',
      noRecords: 'कोणतीही नोंद आढळली नाही',
      allergies: 'अ‍ॅलर्जी',
      chronicConditions: 'दीर्घकालीन आजार',
      emergencyContact: 'आपत्कालीन संपर्क',
      noAllergies: 'कोणतीही औषध अ‍ॅलर्जी नाही',
      noChronic: 'काहीही नोंदवले नाही',
      viewSummary: 'सारांश पहा',
    },
    chat: {
      clinicalIntake: 'क्लिनिकल तपासणी व सल्लामसलत',
      chatCompleted: 'संभाषण पूर्ण झाले',
      inputPlaceholder: 'तुमच्या भाषेत बोला किंवा तुमची लक्षणे टाइप करा...',
      send: 'पाठवा',
      listening: 'ऐकत आहे...',
      speak: 'व्हॉइस इनपुट',
      suggestedQuickReplies: 'सुचवलेली उत्तरे',
      finishChat: 'सल्लामसलत पूर्ण करा',
      continueChat: 'सल्लामसलत सुरू ठेवा',
      restartChat: 'पुन्हा सुरू करा',
      chatEndedTitle: 'सल्लामसलत पूर्ण झाली आहे',
      chatEndedDesc: 'तुमची लक्षणे आणि वैद्यकीय इतिहास नोंदवला गेला आहे.',
      generatingQuestion: 'वैद्यकीय प्रश्न तयार होत आहे...',
      endIntentPrompt: 'तुम्हाला संभाषण समाप्त करायचे आहे का?',
      endIntentYes: 'होय, संभाषण समाप्त करा',
      endIntentNo: 'नाही',
    },
    scanner: {
      title: 'वैद्यकीय कागदपत्र स्कॅनर',
      uploaded: 'अपलोड केले',
      takePhoto: 'फोटो घ्या',
      uploadFile: 'फाइल निवडा',
      dropzone: 'प्रिस्क्रिप्शन किंवा अहवाल येथे ड्रॅग करा किंवा क्लिक करा',
      scanning: 'कागदपत्र स्कॅन होत आहे...',
    },
    doctor: {
      hprVerified: 'HPR सत्यापित चिकित्सक',
      signOut: 'साइन आउट',
      stationTitle: 'डॉक्टर क्लिनिकल निरीक्षण केंद्र (EHR डेटाबेस)',
      clinicalReviewTitle: 'क्लिनिकल पुनरावलोकन आणि रुग्ण नोंदी नोंदणी',
      portalDescription: 'डेटाबेसमध्ये नोंदणीकृत सर्व रुग्णांशी जोडलेले हेल्थकेअर पोर्टल. AI डिजिटलाइझ्ड ट्रान्सक्रिप्ट आणि प्रिस्क्रिप्शन तपासा.',
      opdRegular: 'OPD नियमित',
      dutyActive: 'ड्युटी: सक्रिय',
      today: 'आज',
      registeredPatients: 'नोंदणीकृत रुग्ण',
      liveRecords: 'लाइव्ह डेटाबेस नोंदी',
      triageRedFlags: 'तातडीचे रेड फ्लॅग',
      urgentAttention: 'तातडीचे लक्ष आवश्यक',
      vitalsStable: 'सर्व लक्षणे स्थिर',
      digitizedPrescriptions: 'डिजिटलाइझ्ड औषधपत्रिका',
      verifiedOcr: 'AI व्हिजन OCR द्वारे सत्यापित',
      consultationSessions: 'सल्लामसलत सत्रे',
      aiIntakeConsultations: 'AI इनटेक सल्लामसलत',
      searchPlaceholder: 'नाव किंवा ABHA ID द्वारे रुग्ण शोधा...',
      redFlagsOnly: 'फक्त रेड फ्लॅग्ज',
      withDocuments: 'कागदपत्रांसह',
      refreshRecords: 'रुग्ण नोंदी रीफ्रेश करा',
      patientQueue: 'थेट रुग्ण पुनरावलोकन रांग',
      colPatient: 'रुग्ण',
      colDemographics: 'लोकसंख्याशास्त्र',
      colTriage: 'ट्रायज स्थिती',
      colDocuments: 'कागदपत्रे',
      colActions: 'कृती',
      noPatients: 'कोणतेही रुग्ण आढळले नाहीत.',
      reviewDossier: 'डोसियर तपासा',
      tabConsultations: 'AI सल्लामसलत',
      tabPrescriptions: 'औषधपत्रिका',
      tabMedications: 'औषधे',
      tabBaseline: 'आयुष आणि बेसलाइन',
      closeDossier: 'डोसियर बंद करा',
      patientLanguage: 'रुग्णाची भाषा',
      translateTo: 'भाषांतर करा',
      translating: 'भाषांतर होत आहे...',
      showOriginal: 'मूळ मजकूर',
      showTranslated: 'भाषांतरित',
      translatedBadge: 'AI क्लिनिकल भाषांतर',
      differentLanguageNotice: 'हा सल्ला दुसऱ्या भाषेत नोंदवला गेला होता.',
      translationUnavailable: 'भाषांतर उपलब्ध नाही',
      selectLanguage: 'भाषा निवडा',
    },
    summary: {
      title: 'वैद्यकीय सत्र सारांश',
      subtitle: 'डॉक्टरांच्या पुनरावलोकनासाठी सज्ज',
      print: 'प्रिंट करा',
      close: 'सारांश बंद करा',
      aiTab: 'एआय सारांश',
      detailsTab: 'तपशील',
      generatePromptTitle: 'एआय सारांश तयार करा',
      generatePromptDesc: 'संजीवनी एआय तुमच्या संभाषणाचा इतिहास आणि कागदपत्रांचे डॉक्टरांसाठी संरचित सारांशात संकलन करेल.',
      generateButton: 'सारांश तयार करा',
      loadingTitle: 'संजीवनी एआय तुमचा वैद्यकीय सारांश तयार करत आहे…',
      loadingDesc: 'संभाषण आणि स्कॅन केलेली कागदपत्रे तपासली जात आहेत',
      doctorReviewNote: 'डॉक्टरांच्या पुनरावलोकनासाठी तयार केलेला सारांश',
      regenerate: 'पुन्हा तयार करा',
      noData: 'अद्याप कोणतीही वैद्यकीय माहिती गोळा केलेली नाही. सल्लामसलत सुरू करा किंवा कागदपत्रे अपलोड करा.',
      patientInfo: 'रुग्णाची माहिती',
      chiefComplaint: 'मुख्य तक्रार',
      history: 'सध्याच्या आजाराचा इतिहास',
      clinicalNarrative: 'वैद्यकीय सारांश',
      documents: 'कागदपत्रे आणि तपासण्या',
      ayushAssessment: 'आयुष मूल्यांकन',
      redFlags: 'तातडीचा इशारा',
      recommendations: 'शिफारसी आणि पुढील पावले',
      patientInformation: 'रुग्ण तपशील',
      age: 'वय',
      years: 'वर्षे',
      gender: 'लिंग',
      language: 'भाषा',
      duration: 'कालावधी',
      uploadedDocuments: 'अपलोड केलेली कागदपत्रे',
      conversationLog: 'संभाषण नोंद',
      patientTurns: 'रुग्णाचे म्हणणे',
      medications: 'औषधे',
      labResults: 'लॅब निकाल',
      parameter: 'घटक',
      value: 'मूल्य',
      unit: 'एकक',
      status: 'स्थिती',
      abnormal: 'असामान्य',
      normal: 'सामान्य',
      noEntities: 'या कागदपत्रातून कोणतीही माहिती आढळली नाही.',
      noSessionData: 'अजून कोणताही सत्र डेटा संकलित केलेला नाही.',
      noSessionDataDesc: 'येथे तपशील पाहण्यासाठी संभाषण सुरू करा किंवा दस्तऐवज स्कॅन करा.',
      done: 'पूर्ण',
      footerBrand: 'संजीवनी एआय द्वारे व्युत्पन्न — आयुष मंत्रालय · SIH 2026',
    },
  },

  gu: {
    auth: {
      title: 'ABHA વડે સાઇન ઇન કરો',
      subtitle: 'આયુષ્માન ભારત હેલ્થ એકાઉન્ટ અને OTP પ્રમાણીકરણ',
      doctorTitle: 'ડોક્ટર પોર્ટલ • HP ID વડે સાઇન ઇન કરો',
      doctorSubtitle: 'નેશનલ હેલ્થકેર પ્રોફેશનલ્સ રજિસ્ટ્રી (HPR) અને OTP પ્રમાણીકરણ',
      patient: 'દર્દી',
      doctor: 'ડોક્ટર',
      patientRoleTag: 'દર્દી (Patient)',
      doctorRoleTag: 'ડોક્ટર (Doctor)',
      patientAbhaLabel: 'દર્દી ABHA ID / હેલ્થ કાર્ડ નંબર',
      doctorAbhaLabel: 'HP ID (આરોગ્ય વ્યાવસાયિક ID)',
      abhaFormat: 'પ્રમાણભૂત 14-અંકનું રાષ્ટ્રીય ઓળખ ફોર્મેટ (દા.ત. 14-2345-6789-1011)',
      doctorFormat: 'ફોર્મેટ: HP-[રાજ્ય]-[નંબર] (દા.ત. HP-MH-84729)',
      requestOtp: 'ABHA OTP મોકલો',
      doctorRequestOtp: 'HP OTP મોકલો',
      sendingOtp: 'OTP મોકલાઈ રહ્યો છે...',
      welcome: 'સ્વાગત છે',
      otpSent: 'ABHA OTP મોકલાઈ ગયો છે',
      doctorOtpSent: 'ડોક્ટર OTP મોકલાઈ ગયો છે',
      changeAbha: 'ABHA બદલો',
      changeHpId: 'HP ID બદલો',
      sentToMobile: 'નોંધાયેલા મોબાઇલ પર મોકલ્યો',
      enterOtp: '6-અંકનો OTP દાખલ કરો',
      verifyAndEnter: 'ચકાસો અને પ્રવેશ કરો',
      verifying: 'ચકાસણી ચાલુ છે...',
      register: 'નોંધણી કરો',
      registerPatientPrompt: 'શું તમારી પાસે હજુ સુધી ABHA ID નથી?',
      registerPatientDesc: 'ડિજિટલ હેલ્થ કાર્ડ બનાવવા માટે તરત જ નોંધણી કરો.',
      registerPatientButton: 'નોંધણી કરો અને ABHA ID બનાવો',
      registerDoctorPrompt: 'HPR પર હજુ સુધી નોંધણી કરાવી નથી?',
      registerDoctorDesc: 'ક્લિનિકલ પોર્ટલ મેળવવા માટે આરોગ્ય વ્યાવસાયિક તરીકે નોંધણી કરો.',
      registerDoctorButton: 'આરોગ્ય વ્યાવસાયિક (HP ID) નોંધણી કરો',
      footer: 'સંજીવની ક્લિનિકલ AI • ભારતીય આરોગ્ય કેન્દ્રો અને આયુષ કેન્દ્રો માટે રચાયેલ',
    },
    header: {
      dashboard: 'ડેશબોર્ડ',
      profile: 'પ્રોફાઇલ',
      viewSummary: 'સારાંશ જુઓ',
      submitDetails: 'વિગતો સાચવો',
      saving: 'સાચવી રહ્યું છે...',
      walkInPatient: 'દર્દી',
      abhaLinked: 'ABHA લિંક થયેલ છે',
      changeLanguage: 'ભાષા બદલો',
    },
    dashboard: {
      abhaLinked: 'ABHA લિંક થયેલ છે',
      profile: 'પ્રોફાઇલ',
      addDetails: 'લક્ષણો ઉમેરો',
      signOut: 'સાઇન આઉટ',
      personalDetails: 'વ્યક્તિગત વિગતો',
      activeMedications: 'હાલની દવાઓ',
      pastMedications: 'અગાઉની દવાઓ',
      recentIntakeSessions: 'તાજેતરની સલાહ સત્રો',
      medicalDocuments: 'તબીબી દસ્તાવેજો',
      startIntakeConsultation: 'નવી સલાહ શરૂ કરો',
      noRecords: 'કોઈ રેકોર્ડ મળ્યો નથી',
      allergies: 'એલર્જી',
      chronicConditions: 'લાંબા ગાળાના રોગો',
      emergencyContact: 'ઇમરજન્સી સંપર્ક',
      noAllergies: 'કોઈ દવાની એલર્જી નથી',
      noChronic: 'કંઈ નોંધાયેલ નથી',
      viewSummary: 'સારાંશ જુઓ',
    },
    chat: {
      clinicalIntake: 'ક્લિનિકલ સલાહ અને લક્ષણ પૂછપરછ',
      chatCompleted: 'વાતચીત પૂર્ણ થઈ',
      inputPlaceholder: 'તમારી ભાષામાં બોલો અથવા લક્ષણો લખો...',
      send: 'મોકલો',
      listening: 'સાંભળી રહ્યા છીએ...',
      speak: 'વોઇસ ઇનપુટ',
      suggestedQuickReplies: 'સૂચવેલા જવાબો',
      finishChat: 'સલાહ પૂર્ણ કરો',
      continueChat: 'વાતચીત ચાલુ રાખો',
      restartChat: 'ફરીથી શરૂ કરો',
      chatEndedTitle: 'સલાહ પૂર્ણ થઈ છે',
      chatEndedDesc: 'તમારા લક્ષણો અને તબીબી ઇતિહાસ નોંધવામાં આવ્યો છે.',
      generatingQuestion: 'પ્રશ્ન તૈયાર થઈ રહ્યો છે...',
      endIntentPrompt: 'શું તમે વાતચીત સમાપ્ત કરવા માંગો છો?',
      endIntentYes: 'હા, વાતચીત સમાપ્ત કરો',
      endIntentNo: 'ના',
    },
    scanner: {
      title: 'મેડિકલ દસ્તાવેજ સ્કેનર',
      uploaded: 'અપલોડ કરેલ',
      takePhoto: 'ફોટો લો',
      uploadFile: 'ફાઇલ અપલોડ',
      dropzone: 'પ્રિસ્ક્રિપ્શન અથવા રિપોર્ટ અહીં ખેંચો અથવા ક્લિક કરો',
      scanning: 'દસ્તાવેજ સ્કેન થઈ રહ્યો છે...',
    },
    doctor: {
      hprVerified: 'HPR પ્રમાણિત તબીબ',
      signOut: 'સાઇન આઉટ',
      stationTitle: 'તબીબી દેખરેખ કેન્દ્ર (EHR ડેટાબેઝ)',
      clinicalReviewTitle: 'ક્લિનિકલ સમીક્ષા અને દર્દી રેકોર્ડ રજિસ્ટ્રી',
      portalDescription: 'ડેટાબેઝમાં નોંધાયેલા તમામ દર્દીઓ સાથે જોડાયેલ આરોગ્ય પ્રદાતા પોર્ટલ. AI ડિજિટાઇઝ્ડ પ્રિસ્ક્રિપ્શન તપાસો.',
      opdRegular: 'OPD નિયમિત',
      dutyActive: 'ફરજ: સક્રિય',
      today: 'આજે',
      registeredPatients: 'નોંધાયેલા દર્દીઓ',
      liveRecords: 'લાઇવ ડેટાબેઝ રેકોર્ડ્સ',
      triageRedFlags: 'તાત્કાલિક રેડ ફ્લેગ',
      urgentAttention: 'તાત્કાલિક ધ્યાનની જરૂર છે',
      vitalsStable: 'તમામ પરિમાણો સ્થિર છે',
      digitizedPrescriptions: 'ડિજિટાઇઝ્ડ પ્રિસ્ક્રિપ્શન્સ',
      verifiedOcr: 'AI વિઝન OCR દ્વારા ચકાસાયેલ',
      consultationSessions: 'પરામર્શ સત્રો',
      aiIntakeConsultations: 'AI પ્રાથમિક પરામર્શ',
      searchPlaceholder: 'નામ અથવા ABHA ID દ્વારા દર્દી શોધો...',
      redFlagsOnly: 'માત્ર રેડ ફ્લેગ્સ',
      withDocuments: 'દસ્તાવેજો સાથે',
      refreshRecords: 'દર્દી રેકોર્ડ રિફ્રેશ કરો',
      patientQueue: 'લાઇવ દર્દી સમીક્ષા કતાર',
      colPatient: 'દર્દી',
      colDemographics: 'જનસાंख्यिकी',
      colTriage: 'ટ્રાયજ સ્થિતિ',
      colDocuments: 'દસ્તાવેજો',
      colActions: 'પગલાં',
      noPatients: 'કોઈ દર્દી મળ્યા નથી.',
      reviewDossier: 'ડોઝિયર જુઓ',
      tabConsultations: 'AI પરામર્શ',
      tabPrescriptions: 'પ્રિસ્ક્રિપ્શન્સ',
      tabMedications: 'દવાઓ',
      tabBaseline: 'આયુષ અને બેઝલાઇન',
      closeDossier: 'ડોઝિયર બંધ કરો',
      patientLanguage: 'દર્દીની ભાષા',
      translateTo: 'અનુવાદ કરો',
      translating: 'અનુવાદ થઈ રહ્યો છે...',
      showOriginal: 'મૂળ લખાણ',
      showTranslated: 'અનુવાદિત',
      translatedBadge: 'AI ક્લિનિકલ અનુવાદ',
      differentLanguageNotice: 'આ પરામર્શ અન્ય ભાષામાં નોંધાયેલ છે.',
      translationUnavailable: 'અનુવાદ ઉપલબ્ધ નથી',
      selectLanguage: 'ભાષા પસંદ કરો',
    },
    summary: {
      title: 'તબીબી સત્ર સારાંશ',
      subtitle: 'ડૉક્ટરની સમીક્ષા માટે તૈયાર',
      print: 'પ્રિન્ટ કરો',
      close: 'સારાંશ બંધ કરો',
      aiTab: 'AI સારાંશ',
      detailsTab: 'વિગતો',
      generatePromptTitle: 'AI સારાંશ બનાવો',
      generatePromptDesc: 'સંજીવની AI તમારા સંવાદનો ઇતિહાસ અને અપલોડ કરેલા દસ્તાવેજોને ડૉક્ટર માટે સંરચિત સારાંશમાં સંકલિત કરશે.',
      generateButton: 'સારાંશ બનાવો',
      loadingTitle: 'સંજીવની AI તમારો તબીબી સારાંશ તૈયાર કરી રહ્યું છે…',
      loadingDesc: 'સંવાદ અને સ્કેન કરેલા દસ્તાવેજોની સમીક્ષા થઈ રહી છે',
      doctorReviewNote: 'ડૉક્ટરની સમીક્ષા માટે બનાવેલ સારાંશ',
      regenerate: 'ફરીથી બનાવો',
      noData: 'હજી સુધી કોઈ તબીબી માહિતી એકત્રિત થઈ નથી. પરામર્શ શરૂ કરો અથવા દસ્તાવેજ અપલોડ કરો.',
      patientInfo: 'દર્દીની માહિતી',
      chiefComplaint: 'મુખ્ય ફરિયાદ',
      history: 'હાલની બીમારીનો ઇતિહાસ',
      clinicalNarrative: 'તબીબી વિવરણ',
      documents: 'દસ્તાવેજો અને તપાસણી',
      ayushAssessment: 'આયુષ મૂલ્યાંકન',
      redFlags: 'ઇમરજન્સી ચેતવણી',
      recommendations: 'ભલામણો અને આગળના પગલાં',
      patientInformation: 'દર્દી વિગતો',
      age: 'ઉંમર',
      years: 'વર્ષ',
      gender: 'લિંગ',
      language: 'ભાષા',
      duration: 'સમયગાળો',
      uploadedDocuments: 'અપલોડ કરેલા દસ્તાવેજો',
      conversationLog: 'વાતચીત લોગ',
      patientTurns: 'દર્દીના જવાબો',
      medications: 'દવાઓ',
      labResults: 'લેબ પરિણામો',
      parameter: 'પેરામીટર',
      value: 'મૂલ્ય',
      unit: 'એકમ',
      status: 'સ્થિતિ',
      abnormal: 'અસામાન્ય',
      normal: 'સામાન્ય',
      noEntities: 'આ દસ્તાવેજમાંથી કોઈ માહિતી મળી નથી.',
      noSessionData: 'હજુ સુધી કોઈ સત્ર ડેટા એકત્રિત કરવામાં આવ્યો નથી.',
      noSessionDataDesc: 'વિગતો અહીં જોવા માટે વાતચીત શરૂ કરો અથવા દસ્તાવેજ સ્કેન કરો.',
      done: 'પૂર્ણ',
      footerBrand: 'સંજીવની AI દ્વારા જનરેટ કરાયેલ — આયુષ મંત્રાલય · SIH 2026',
    },
  },
}

export function useTranslation(lang: LanguageCode): TranslationDictionary {
  return TRANSLATIONS[lang] || TRANSLATIONS.en
}
